import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout';
import { Card, Badge, Input, Select, Pagination, ContentLoader } from '@/components/common';
import { MagnifyingGlassIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import api, { getErrorMessage } from '@/services/api';
import { formatDateTime } from '@/utils';

/**
 * The broker's Audit Log (decision 17).
 *
 * This page existed before the trail did: it called `/audit-logs/`, which is
 * not a route on this platform and never was, and rendered its 404 as "No
 * audit logs found." — an authoritative statement that nothing had ever
 * happened. It now reads `/audit/logs/` (`apps.audit.AuditLogViewSet`), which
 * serves the caller's OWN broker's rows and nobody else's, and the three
 * states below are distinct: loading, failed, and genuinely empty.
 */

interface AuditLog {
  id: string;
  timestamp: string;
  user: string | null;
  user_email: string;
  user_role: string;
  actor_name: string;
  action: string;
  action_display: string;
  action_detail: string;
  severity: string;
  entity_type: string;
  object_repr: string;
  ip_address: string | null;
}

interface AuditLogsResponse {
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  results: AuditLog[];
}

/** Mirrors `AuditLog.ACTION_TYPES`; only the ones v1 actually records. */
const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'login', label: 'Signed in' },
  { value: 'logout', label: 'Signed out' },
  { value: 'login_failed', label: 'Failed sign-in' },
  { value: 'create', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'delete', label: 'Deleted' },
  { value: 'export', label: 'Exported' },
  { value: 'permission_change', label: 'Permission change' },
  { value: 'status_change', label: 'Status change' },
];

const actionBadgeVariant: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
  login: 'info',
  logout: 'info',
  login_failed: 'error',
  export: 'warning',
  permission_change: 'warning',
};

export function AuditLogsPage() {
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading, isError, error } = useQuery<AuditLogsResponse>({
    queryKey: ['audit-logs', page, debouncedSearch, action, actor, dateFrom, dateTo],
    queryFn: async () => {
      const params: Record<string, string | number> = { page };
      if (debouncedSearch) params.search = debouncedSearch;
      if (action) params.action = action;
      if (actor) params.actor = actor;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/audit/logs/', { params });
      return res.data;
    },
  });

  const handleFilterChange = () => {
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title="Audit Log"
        subtitle="Who did what, when, and from where"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Audit Log' }]}
      />

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Input
            placeholder="Search details..."
            value={search}
            leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
            onChange={(e) => {
              setSearch(e.target.value);
              handleFilterChange();
            }}
          />
          <Input
            placeholder="Actor (name or email)"
            value={actor}
            onChange={(e) => {
              setActor(e.target.value);
              handleFilterChange();
            }}
          />
          <Select
            options={ACTION_OPTIONS}
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              handleFilterChange();
            }}
          />
          <Input
            type="date"
            placeholder="From date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              handleFilterChange();
            }}
          />
          <Input
            type="date"
            placeholder="To date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              handleFilterChange();
            }}
          />
        </div>
      </Card>

      {/* Results. Three states, never two: a failed request that renders as
          "no audit logs" is the platform telling the reader, with authority,
          that nothing ever happened. */}
      <div className="mt-6">
        {isLoading ? (
          <ContentLoader />
        ) : isError ? (
          <Card>
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-amber-500" />
              <p className="mt-3 font-medium text-gray-900 dark:text-white">
                The audit log could not be loaded.
              </p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{getErrorMessage(error)}</p>
            </div>
          </Card>
        ) : !data?.results?.length ? (
          <Card>
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No activity matches these filters.
            </div>
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              {data.results.map((log) => (
                <Card key={log.id}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant={actionBadgeVariant[log.action] || 'default'} size="sm">
                          {log.action_display || log.action}
                        </Badge>
                        {log.entity_type && (
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {log.entity_type}
                          </span>
                        )}
                        {log.object_repr && (
                          <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                            &mdash; {log.object_repr}
                          </span>
                        )}
                      </div>
                      {log.action_detail && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 break-words">
                          {log.action_detail}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {log.actor_name || log.user_email || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDateTime(log.timestamp)}
                      </p>
                      {log.ip_address && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{log.ip_address}</p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {data.total_pages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={data.current_page}
                  totalPages={data.total_pages}
                  totalItems={data.count}
                  itemsPerPage={data.page_size}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
